import React, { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { ReactComponent as Dots } from '../../assets/img/icons/common/Dots.svg';
import useIsMobile from '../../hooks/useIsMobile';
import { LargeIconButton } from '../Buttons';
import { useHandleOutsideClick } from '../../hooks/useHandleOutsideClick';
import './Dropdown.scss';

export const PageDropdown = ({
  icon = Dots,
  loading,
  options,
  closeByClick = true,
  disabled,
  tooltip,
  className,
  btnClassName,
  direction = 'auto', // top, bottom, auto
  btnColor, // black, red, gray
  container,
}) => {
  const [openUp, setOpenUp] = useState(direction === 'top' || direction === 'auto');
  const [opened, setOpened] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [containerMenuStyle, setContainerMenuStyle] = useState();

  const isMobile = useIsMobile();
  const toggleRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const containerRef = useRef(null);
  const menuContainerRef = useRef(null);

  const onClickOutside = useCallback(() => {
    setOpened(false);
  }, []);

  useHandleOutsideClick(opened, onClickOutside, toggleRef, menuContainerRef);

  const toggleSelectedOptionIndex = (option, optionIndex) => {
    if (option.children?.length) {
      if (selectedOptionIndex === optionIndex) {
        setSelectedOptionIndex(null);
      } else {
        setSelectedOptionIndex(optionIndex);
      }
    }
  };

  const closeDropdown = () => {
    setOpened(false);
    setSelectedOptionIndex(null);
  };

  const getParentScroll = node => {
    const overflowRegex = /(auto|scroll|overlay)/;
    let parent = node.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);

      if (
        (overflowRegex.test(style.overflow) || overflowRegex.test(style.overflowY)) &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  };

  const toggleMenu = () => {
    if (buttonRef.current && direction === 'auto' && !opened) {
      const btnRect = buttonRef.current.getBoundingClientRect();
      const container = getParentScroll(buttonRef.current);

      const containerRect = container
        ? container.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };

      const menuHeight = options.length * 36 + 4 + 4; // menu height is 36 + 4 padding top + 4 padding bottom
      const spaceBelow = containerRect.bottom - btnRect.bottom;
      const spaceAbove = btnRect.top - containerRect.top;
      const newOpenUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

      if (openUp !== newOpenUp) {
        setOpenUp(newOpenUp);
      }
    }

    setOpened(prev => !prev);
  };

  useEffect(() => {
    if (container) {
      containerRef.current = document.querySelector(container);
    }
  }, []);

  useLayoutEffect(() => {
    if (!container) {
      return;
    }

    if (opened) {
      if (containerRef.current && buttonRef.current && menuRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const menuHeight = menuRef.current.offsetHeight;
        const menuWidth = menuRef.current.offsetWidth;
        const halfMenuWidth = menuWidth / 2;
        const halfBtnWidth = buttonRect.width / 2;
        const left = buttonRect.left - containerRect.left - halfMenuWidth + halfBtnWidth;
        const top = buttonRect.top - containerRect.top - menuHeight - 4;

        setContainerMenuStyle({
          top,
          left,
          visibility: 'visible',
        });
      }
    } else {
      if (containerMenuStyle) {
        setContainerMenuStyle(undefined);
      }
    }
  }, [opened]);

  const getMenu = () => {
    return (
      <ul ref={menuRef} className={cx('custom-dropdown__menu-options', { top: openUp })}>
        {options.map((option, i) => {
          let onClick;
          let onMouseEnter;
          let onMouseLeave;

          if (!option.header) {
            onClick = e => {
              if (option.function) {
                option.function(e);

                if (closeByClick) {
                  closeDropdown();
                }
              }

              if (isMobile) {
                toggleSelectedOptionIndex(option, i);
              }
            };

            onMouseEnter = () => {
              if (!isMobile) {
                toggleSelectedOptionIndex(option, i);
              }
            };

            onMouseLeave = () => {
              if (!isMobile) {
                toggleSelectedOptionIndex(option, i);
              }
            };
          }

          return (
            <li
              key={i}
              className={cx('custom-dropdown__item', {
                disabled: !!option.disabled,
                active: !!option.active,
                header: !!option.header,
              })}
              style={{ color: option.color || '#1A1A1A' }}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {option.label}

              {selectedOptionIndex === i && option.children?.length ? (
                <div className="custom-dropdown__menu-options-children-container">
                  <ul className="custom-dropdown__menu-options">
                    {option.children.map((childOption, childIndex) => {
                      if (childOption.hidden) {
                        return null;
                      }

                      return (
                        <li
                          key={`${i}-${childIndex}`}
                          className={cx('custom-dropdown__item', { disabled: childOption.disabled })}
                          style={{ color: childOption.color || '#1A1A1A' }}
                          onClick={() => {
                            if (option.onChildSelect) {
                              option.onChildSelect(childOption.payload);

                              if (closeByClick) {
                                closeDropdown();
                              }
                            }
                          }}
                        >
                          {childOption.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderMenu = () => {
    const menu = getMenu();

    if (containerRef.current) {
      return createPortal(
        <div
          className="custom-dropdown__menu-options-container"
          ref={menuContainerRef}
          style={containerMenuStyle}
        >
          {menu}
        </div>,
        containerRef.current
      );
    }

    return menu;
  };

  return (
    <div
      ref={toggleRef}
      className={cx('custom-dropdown__container', { opened: opened, disabled, [className]: !!className })}
    >
      <div className="custom-dropdown__toggle">
        <LargeIconButton
          Icon={icon}
          isLoading={loading}
          className={btnClassName}
          tooltip={!opened ? tooltip : undefined}
          btnRef={buttonRef}
          type={btnColor}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              toggleMenu();

              if (opened) {
                setSelectedOptionIndex(null);
              }
            }
          }}
        />
      </div>

      {opened ? renderMenu() : null}
    </div>
  );
};
